import { useState } from "react";
import { View } from "react-native";
import type { ImageSourcePropType } from "react-native";
import type {
  CoverageState,
  InsurancePolicy,
  FertilityCoverage as FertilityCoverageType,
  VerificationStatus,
} from "../../../../../../packages/coverage/hooks/useCoverage";
import { ClinicalDataStatus, Row } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { EMBER } from "../../../../onboarding/theme";
import {
  GroupedCard,
  YouIllustration,
  YOU_ILLUSTRATION_SIZE,
} from "../components";
import { ICONS } from "../icons";
import EmployerBenefitsModal from "../modals/EmployerBenefitsModal";
import InsuranceInfoModal from "../modals/InsuranceInfoModal";
import EmployerBenefitsCard from "./EmployerBenefitsCard";
import InsuranceCard from "./InsuranceCard";

function CoverageRow({
  source,
  title,
  subtitle,
  onPress,
}: {
  source: ImageSourcePropType;
  title: string;
  subtitle: string;
  onPress?: () => void;
}) {
  return (
    <Row
      surface="plain"
      leading={<YouIllustration source={source} tinted />}
      leadingSize={YOU_ILLUSTRATION_SIZE}
      title={title}
      subtitle={subtitle}
      onPress={onPress}
      action="coverage_row"
      chevron={onPress ? undefined : false}
      gap={14}
      paddingHorizontal={16}
      paddingVertical={12}
      borderRadius={0}
      titleStyle={{
        fontSize: 15,
        lineHeight: 20,
        fontWeight: "500",
        color: EMBER.colors.text,
      }}
      subtitleStyle={{
        fontSize: 12,
        lineHeight: 16,
        color: EMBER.colors.textLight,
      }}
      style={{
        borderRadius: 0,
      }}
    />
  );
}

type Props = {
  policies: InsurancePolicy[];
  primaryPolicy: InsurancePolicy | undefined;
  hasInsurance: boolean;
  coverageState: CoverageState;
  verificationStatus: VerificationStatus;
  fertilityCoverage: FertilityCoverageType | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isRetrying?: boolean;
};

export default function CoverageBenefits({
  policies,
  primaryPolicy,
  hasInsurance,
  coverageState,
  verificationStatus,
  fertilityCoverage,
  isLoading,
  isError,
  onRetry,
  isRetrying = false,
}: Props) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const [modalVisible, setModalVisible] = useState(false);
  const [insuranceModal, setInsuranceModal] = useState<{
    mode: "summary" | "edit";
    policy: InsurancePolicy | undefined;
  }>({ mode: "summary", policy: undefined });
  const [employerBenefitsModalVisible, setEmployerBenefitsModalVisible] =
    useState(false);

  const openInsuranceModal = (
    mode: "summary" | "edit",
    policy?: InsurancePolicy,
  ) => {
    setInsuranceModal({ mode, policy });
    setModalVisible(true);
  };

  const savedPolicies =
    policies.length > 0 ? policies : primaryPolicy ? [primaryPolicy] : [];
  const treatmentCount = fertilityCoverage?.treatmentsCovered?.length ?? 0;
  const fertilitySubtitle = fertilityCoverage?.hasCoverage
    ? t("you.coverage_benefits.treatments_count", {
        params: { count: String(treatmentCount) },
      })
    : verificationStatus === "pending"
      ? t("you.coverage_benefits.fertility_coverage_pending")
      : verificationStatus === "verified"
        ? t("you.coverage_benefits.fertility_coverage_not_found")
        : t("you.coverage_benefits.fertility_coverage_unverified");
  const hasEmployerBenefits =
    patient?.fertilityIntent?.hasEmployerBenefits ?? null;
  const employerBenefit = patient?.fertilityIntent?.employerBenefit ?? null;

  const showSavedInsuranceCards = !isLoading && savedPolicies.length > 0;
  const showEmptyInsuranceCard =
    !isLoading && !showSavedInsuranceCards && !(isError && !hasInsurance);

  // Employer benefits live on the patient profile, not the coverage endpoint.
  // Keep that control available if insurance is still loading or unavailable.
  const showFertilityCoverageRow = showSavedInsuranceCards;

  const providerName = (policy: InsurancePolicy) =>
    policy.payer?.displayName ??
    policy.payer?.name ??
    policy.insuranceCarrier ??
    null;

  const isPolicyActive = (policy: InsurancePolicy, index: number) => {
    if (index === 0) return coverageState === "active";
    if (!policy.terminationDate) return true;
    const terminationDate = new Date(policy.terminationDate);
    return (
      Number.isNaN(terminationDate.getTime()) || terminationDate >= new Date()
    );
  };

  return (
    <View>
      {isError ? (
        showSavedInsuranceCards ? (
          <ClinicalDataStatus
            state="stale"
            message={t("you.coverage_benefits.coverage_load_failed")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={onRetry}
            isRetrying={isRetrying}
            style={{ marginBottom: 12 }}
            testID="coverage-stale"
          />
        ) : (
          <ClinicalDataStatus
            state="unavailable"
            title={t("you.coverage_benefits.coverage_load_failed")}
            message={t("states.check_connection_and_try_again")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={onRetry}
            isRetrying={isRetrying}
            style={{ marginBottom: 12 }}
            testID="coverage-unavailable"
          />
        )
      ) : null}

      {isLoading ? (
        // Keep the insurance card in the layout while the app-wide prefetch
        // resolves. The dialog owns the eventual form/summary decision, so a
        // quick tap remains safe without making the card look unavailable.
        <InsuranceCard
          insuranceInfo={{ provider: null, planName: null, memberId: null }}
          onAdd={() => openInsuranceModal("edit")}
        />
      ) : showSavedInsuranceCards ? (
        savedPolicies.map((policy, index) => (
          <InsuranceCard
            key={policy.id}
            insuranceInfo={{
              provider: providerName(policy),
              planName: policy.planName,
              memberId: policy.memberId,
            }}
            hasInsurance
            isActive={isPolicyActive(policy, index)}
            verificationStatus={verificationStatus}
            onEdit={() => openInsuranceModal("edit", policy)}
          />
        ))
      ) : showEmptyInsuranceCard ? (
        <InsuranceCard
          insuranceInfo={{ provider: null, planName: null, memberId: null }}
          onAdd={() => openInsuranceModal("edit")}
        />
      ) : null}

      <EmployerBenefitsCard
        hasEmployerBenefits={hasEmployerBenefits}
        employerBenefit={employerBenefit}
        bottomSpacing={showFertilityCoverageRow ? 24 : 0}
        onPress={() => setEmployerBenefitsModalVisible(true)}
      />

      {showFertilityCoverageRow ? (
        <GroupedCard>
          <CoverageRow
            source={ICONS.sparkle}
            title={t("you.coverage_benefits.fertility_coverage_label")}
            subtitle={fertilitySubtitle}
            onPress={() => openInsuranceModal("summary", savedPolicies[0])}
          />
        </GroupedCard>
      ) : null}

      <InsuranceInfoModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        primaryPolicy={insuranceModal.policy}
        hasInsurance={insuranceModal.policy !== undefined}
        verificationStatus={verificationStatus}
        fertilityCoverage={fertilityCoverage}
        isLoading={isLoading}
        initialMode={insuranceModal.mode}
      />
      <EmployerBenefitsModal
        visible={employerBenefitsModalVisible}
        onClose={() => setEmployerBenefitsModalVisible(false)}
      />
    </View>
  );
}
